import { Action } from "redux";
import { call, fork, put, select, takeEvery } from "redux-saga/effects";

import { createProcessMenuItemCustomActionAction, IMenuItemCustomActionProcessedAction, MENU_ITEM_CUSTOM_ACTION_PROCESSED } from "@multiweb/reactCore/bridge";
import { EMPTY_OBJECT, takeResultAction } from "@multiweb/reactUtils";
import { IMainMenuFormActionData, IMainMenuItemActionParams, IMainMenuNotificationGroupActionData, MainMenuItemActionType } from "@multiweb/types/multiweb";
import {
    IResultActionError,
    IResultActionSuccess,
    SagaWithReturnType,
    SagaYieldReturn,
} from "@multiweb/types/react";
import { createMenuItemActivatedAction, createMenuItemDefaultActionAllowedAction } from "../actions";
import { logger } from "../logger";
import { getFormItem, getFormItemByProfileId, getProfileFormRelationship } from "../selectors";
import { IMenuItemActivatedAction, MenuItemActivatedType } from "../types";
import { formCompatibleItems, supportedMenuItemTypes } from "./constants";
import { activationTypeToMenuItemType, createMainMenuItemActionParams } from "./utils";

/**
 * Vzor odchytávájící aktivaci položek menu, které podporují custom akce.
 * @param action
 */
const supportedMenuItemActivatedActionPattern = (action: Action) => action instanceof IMenuItemActivatedAction && supportedMenuItemTypes.includes(action.payload.itemType);

/**
 * Vzor odchytávájící aktivaci položek menu, které nepodporují custom akce.
 * @param action
 */
const unsupportedMenuItemActivatedActionPattern = (action: Action) => action instanceof IMenuItemActivatedAction && !supportedMenuItemTypes.includes(action.payload.itemType);

export const saga: SagaWithReturnType = function* customActionsSaga() {
    yield takeEvery(unsupportedMenuItemActivatedActionPattern, processUnsupportedMenuItemActivatedSaga);
    yield takeEvery(supportedMenuItemActivatedActionPattern, processSupportedMenuItemActivatedSaga);
};

/**
 * Obsluhuje zpracování nepodporovaných položek. Vyšle akci k provedení výchozího chování.
 * @param action
 */
export const processUnsupportedMenuItemActivatedSaga: SagaWithReturnType<[IMenuItemActivatedAction]> = function* processUnsupportedMenuItemActivatedSaga(action) {
    yield put(createMenuItemDefaultActionAllowedAction(action));
};

/**
 * Obsluhuje volání funkce pro vlastní zpracování aktivace položky menu.
 * @param action Akce aktivace položky menu.
 */
export const processSupportedMenuItemActivatedSaga: SagaWithReturnType<[IMenuItemActivatedAction]> = function* processMenuItemActivatedSaga(action) {
    const menuItemParams: SagaYieldReturn<typeof createMenuItemParamsSaga> = yield call(createMenuItemParamsSaga, action);

    if (!menuItemParams) {
        return;
    }
    yield put(createProcessMenuItemCustomActionAction(menuItemParams));
    yield fork(takeResultAction, MENU_ITEM_CUSTOM_ACTION_PROCESSED, customActionSuccessSaga, customActionErrorSaga, action, menuItemParams);
};

/**
 * Vytváří parametry pro zpracování custom akce. Podle typu položky volá ságu pro získání konkrétních parametrů.
 * @param action Akce aktivace položky menu.
 */
export const createMenuItemParamsSaga: SagaWithReturnType<[IMenuItemActivatedAction], IMainMenuItemActionParams | undefined> = function* (action) {
    const { itemType } = action.payload;
    let mainMenuItemActionParams: undefined | SagaYieldReturn<typeof createMainMenuItemActionParams>;
    if (itemType === MenuItemActivatedType.notificationGroup) {
        const notificationGroupParams: SagaYieldReturn<typeof createNotificationGroupParamsSaga> = yield call(createNotificationGroupParamsSaga, action);
        mainMenuItemActionParams = yield call(createMainMenuItemActionParams, MainMenuItemActionType.notificationGroup, notificationGroupParams);
    } else if (formCompatibleItems.includes(itemType)) {
        const formItemParams: SagaYieldReturn<typeof createFormItemParamsSaga> = yield call(createFormItemParamsSaga, action);
        mainMenuItemActionParams = yield call(createMainMenuItemActionParams, MainMenuItemActionType.form, formItemParams);
    } else if (itemType === MenuItemActivatedType.logoButton) {
        mainMenuItemActionParams = yield call(createMainMenuItemActionParams, MainMenuItemActionType.logoButton, EMPTY_OBJECT);
    }
    return mainMenuItemActionParams;
};

/**
 * Převádí parametry ze spouštěcí akce na parametry pro zpracování custom akce skupiny notifikací.
 * @param action Akce aktivace položky menu.
 */
// eslint-disable-next-line require-yield
export const createNotificationGroupParamsSaga: SagaWithReturnType<[IMenuItemActivatedAction], IMainMenuNotificationGroupActionData | undefined> = function* createNotificationGroupParamsSaga(
    action,
) {
    return { id: action.payload.itemId as string };
};

type ProfileData = {profileId: number; formItemId: number} | undefined;

/**
 * Převádí parametry ze spouštěcí akce na parametry pro zpracování custom akce formuláře.
 * @param action Akce aktivace položky menu.
 */
export const createFormItemParamsSaga: SagaWithReturnType<[IMenuItemActivatedAction], IMainMenuFormActionData | undefined> = function* createFormItemParamsSaga(action) {
    const { itemId, itemType } = action.payload;
    let formItemId, profileId;
    if (itemType === MenuItemActivatedType.profile) {
        const idsFromProfileAction: SagaYieldReturn<typeof getIdsFromProfileActivationSaga> = yield call(getIdsFromProfileActivationSaga, itemId as string);
        if (idsFromProfileAction) {
            formItemId = idsFromProfileAction.formItemId;
            profileId = idsFromProfileAction.profileId;
        } else {
            return undefined;
        }
    } else {
        formItemId = itemId as number;
        profileId = undefined;
    }

    const formItem: ReturnType<typeof getFormItem> = yield select(getFormItem, formItemId);
    if (!formItem) {
        yield call(logger.warn, "Cannot process custom action for menu item with id %s, because item with this id was not found.", formItemId);
        return undefined;
    }
    const menuItemType: ReturnType<typeof activationTypeToMenuItemType> = yield call(activationTypeToMenuItemType, itemType);
    return {
        menuId: formItem.id,
        menuItemType,
        formCode: formItem.formCode,
        code: formItem.code,
        openMode: formItem.openMode,
        profileId,
        fromSideButton: itemType === MenuItemActivatedType.panelItemCombinedItemFolder,
    };
};

/**
 * Získá id profilu a formulářové položky na základě id aktivované položky profilu.
 * @param itemId
 */
export const getIdsFromProfileActivationSaga: SagaWithReturnType<[string], ProfileData> = function* getIdsFromProfileActivationSaga(itemId) {
    const profileFormRelationship: ReturnType<typeof getProfileFormRelationship> = yield select(getProfileFormRelationship, itemId);
    if (!profileFormRelationship) {
        yield call(logger.error, "Cannot get profile and form relationship for activated item with id %s.", itemId);
        return undefined;
    }
    const { profileId, formItemId: relatedFormItemId } = profileFormRelationship;
    let formItemId;
    if (relatedFormItemId) {// aktivovaný profil je zobrazený jako potomek formulářové položky, použije se její id
        formItemId = relatedFormItemId;
    } else {// aktivovaný profil je zobrazený samostatně, formulář se vyhledá podle formCode
        const formItem: ReturnType<typeof getFormItemByProfileId> = yield select(getFormItemByProfileId, profileId);
        if (formItem) {
            formItemId = formItem.id;
        } else {
            yield call(logger.error, "Form item for profile with id %s was not found.", profileId);
            return undefined;
        }
    }

    return { profileId, formItemId };
};

/**
 * Obsluhuje výsledek úspěšného zpracování custom akce.
 * @param successAction Akce úspěšného zpracování custom akce.
 * @param menuItemActivatedAction Akce aktivace položky menu, která spustila zpracování custom akce.
 */
export const customActionSuccessSaga: SagaWithReturnType<[IResultActionSuccess<IMenuItemCustomActionProcessedAction>, IMenuItemActivatedAction]> = function* customActionSuccessSaga(
    successAction,
    menuItemActivatedAction,
) {
    if (!successAction.payload.preventDefault) {
        const { itemId, itemType, section, mouseEvent, keyEvent } = menuItemActivatedAction.payload;
        const { preventFormDefault } = successAction.payload;
        yield put(createMenuItemDefaultActionAllowedAction(createMenuItemActivatedAction(itemId, itemType, section, mouseEvent, keyEvent, preventFormDefault)));
    }
};

/**
 * Obsluhuje výsledek chybového zpracování custom akce. Pokud je chyba způsobena tím, že není zaregistrovaná
 * žádná obsluha custom akcí, tak to není považováno za chybu a provede se výchozí chování aktivované položky.
 * @param errorAction Akce chybového zpracování custom akce.
 * @param menuItemActivatedAction Akce aktivace položky menu, která spustila zpracování custom akce.
 * @param menuItemParams Parametry, které se předávaly pro zpracování custom akce.
 */
export const customActionErrorSaga: SagaWithReturnType<[IResultActionError<IMenuItemCustomActionProcessedAction>, IMenuItemActivatedAction, IMainMenuItemActionParams]> = function* customActionErrorSaga(
    errorAction,
    menuItemActivatedAction,
    menuItemParams,
) {
    if (errorAction.payload.handlerNotFound) {
        yield put(createMenuItemDefaultActionAllowedAction(menuItemActivatedAction));
    } else {
        yield call(
            logger.error,
            "Processing of custom actions for menu item %O failed with error: %s.",
            menuItemParams,
            errorAction.payload.errorMessage,
        );
    }
};
